import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

/**
 * Keeps quick-add product galleries manual and swipeable. Product pages may
 * autoplay media, but the modal should not keep revealing extra images below
 * the first slide while a customer is choosing a pack.
 * @param {ParentNode} root
 */
function prepareQuickAddMediaGallery(root) {
  const slideshow = root.querySelector('slideshow-component');
  if (!slideshow) return;

  slideshow.setAttribute('paused', '');
  slideshow.removeAttribute('autoplay');
  slideshow.setAttribute('aria-live', 'off');

  if (typeof slideshow.pause === 'function') {
    slideshow.pause();
  }

  const firstImage = root.querySelector('img');
  if (firstImage instanceof HTMLImageElement) {
    firstImage.loading = 'eager';
    firstImage.fetchPriority = 'high';
    firstImage.decoding = 'async';
  }
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Promise<void> | null} */
  #productPageFetchPromise = null;
  /** @type {string | null} */
  #productPageFetchUrl = null;
  /** @type {Document | null} */
  #cachedProductHtml = null;
  /** @type {string | null} */
  #cachedProductPageUrl = null;

  get cachedProductHtml() {
    return this.#getCachedProductHtml(this.productPageUrl);
  }

  get productPageUrl() {
    return /** @type {HTMLAnchorElement} */ (this.closest('product-card')?.querySelector('a[ref="productCardLink"]'))
      ?.href;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    this.addEventListener('pointerenter', this.#prefetchProductPage);
    this.addEventListener('focusin', this.#prefetchProductPage);
    this.addEventListener('touchstart', this.#prefetchProductPage, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.removeEventListener('pointerenter', this.#prefetchProductPage);
    this.removeEventListener('focusin', this.#prefetchProductPage);
    this.removeEventListener('touchstart', this.#prefetchProductPage);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    let productPageUrl = this.productPageUrl;
    await this.fetchProductPage(productPageUrl);

    // A product-card swatch can change the URL while a prefetched request is
    // settling. Always render the product represented by the card now.
    if (this.#normalizeProductPageUrl(productPageUrl) !== this.#normalizeProductPageUrl(this.productPageUrl)) {
      productPageUrl = this.productPageUrl;
      await this.fetchProductPage(productPageUrl);
    }

    const cachedProductHtml = this.#getCachedProductHtml(productPageUrl);
    if (!cachedProductHtml) return;

    // Create a fresh copy of the cached HTML to avoid modifying the original.
    const freshHtmlCopy = new DOMParser().parseFromString(cachedProductHtml.documentElement.outerHTML, 'text/html');
    await this.updateQuickAddModal(freshHtmlCopy, productPageUrl);

    this.#openQuickAddModal();
  };

  #prefetchProductPage = () => {
    this.fetchProductPage(this.productPageUrl).catch((error) => {
      // Keep prefetch non-blocking. The click path retries and surfaces real failures.
      console.debug('Quick add prefetch failed', error);
    });
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string | undefined} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<void>}
   * @throws {Error} If the fetch request fails or returns a non-200 response
   */
  async fetchProductPage(productPageUrl) {
    const normalizedProductPageUrl = this.#normalizeProductPageUrl(productPageUrl);
    if (!normalizedProductPageUrl) return;
    if (this.#cachedProductPageUrl === normalizedProductPageUrl && this.#cachedProductHtml) return;
    if (this.#productPageFetchUrl === normalizedProductPageUrl && this.#productPageFetchPromise) {
      try {
        await this.#productPageFetchPromise;
      } catch (error) {
        if (error.name !== 'AbortError') throw error;
      }
      return;
    }

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    const abortController = new AbortController();
    this.#abortController = abortController;
    this.#productPageFetchUrl = normalizedProductPageUrl;

    const fetchPromise = (async () => {
      const response = await fetch(normalizedProductPageUrl, {
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      // Keep the document and its URL together so a new swatch selection can
      // never reuse the previous product or variant response.
      this.#cachedProductHtml = html;
      this.#cachedProductPageUrl = normalizedProductPageUrl;
    })();
    this.#productPageFetchPromise = fetchPromise;

    try {
      await fetchPromise;
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      } else {
        throw error;
      }
    } finally {
      if (this.#abortController === abortController) {
        this.#abortController = null;
      }
      if (this.#productPageFetchPromise === fetchPromise) {
        this.#productPageFetchPromise = null;
        this.#productPageFetchUrl = null;
      }
    }
  }

  /**
   * @param {string | undefined} productPageUrl
   * @returns {string}
   */
  #normalizeProductPageUrl(productPageUrl) {
    if (!productPageUrl) return '';

    try {
      const normalizedUrl = new URL(productPageUrl, document.baseURI);
      normalizedUrl.hash = '';
      return normalizedUrl.href;
    } catch (_error) {
      return productPageUrl;
    }
  }

  /**
   * @param {string | undefined} productPageUrl
   * @returns {Document | null}
   */
  #getCachedProductHtml(productPageUrl) {
    const normalizedProductPageUrl = this.#normalizeProductPageUrl(productPageUrl);
    if (!normalizedProductPageUrl || normalizedProductPageUrl !== this.#cachedProductPageUrl) return null;

    return this.#cachedProductHtml;
  }

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @param {string | undefined} productPageUrl - The product URL represented by the HTML.
   */
  async updateQuickAddModal(newHtml, productPageUrl = this.productPageUrl) {
    const productGrid = newHtml.querySelector('[data-product-grid-content]');
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      if (!productDetails) return;
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = productPageUrl || '';

      if (!productFormComponent || !variantPicker || !productPrice || !productTitle) return;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      productHeader.appendChild(productPrice);
      productGrid.appendChild(productHeader);
      productGrid.appendChild(variantPicker);
      productGrid.appendChild(productFormComponent);
      productDetails.remove();
    }

    morph(modalContent, productGrid);
    prepareQuickAddMediaGallery(modalContent);
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantGalleryUpdate);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleVariantGalleryUpdate = () => {
    requestAnimationFrame(() => prepareQuickAddMediaGallery(this));
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
